import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Send, Plus } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AdminCMS from "@/pages/AdminCMS";
import AdminBlog from "@/pages/AdminBlog";

const STAGES = ["concept", "revision", "validation", "delivered"];

export default function AdminPortal() {
  const { t } = useLang();
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [tab, setTab] = useState("enquiries");

  const loadAll = useCallback(async () => {
    const [o, e, c, r, th] = await Promise.all([
      api.get("/admin/overview"), api.get("/admin/enquiries"),
      api.get("/admin/clients"), api.get("/admin/requests"), api.get("/admin/threads"),
    ]);
    setOverview(o.data); setEnquiries(e.data); setClients(c.data); setRequests(r.data); setThreads(th.data);
  }, []);

  const openThread = async (clientId) => {
    setActiveThread(clientId);
    const { data } = await api.get(`/admin/messages/${clientId}`);
    setThreadMsgs(data);
    api.get("/admin/threads").then(({ data: th }) => setThreads(th));
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !activeThread) return;
    await api.post(`/admin/messages/${activeThread}`, { body: reply });
    setReply("");
    openThread(activeThread);
  };

  useEffect(() => {
    document.title = "Admin — Bezalel Visionary";
    if (!checking && user === false) navigate("/admin/login");
    if (user && user.role === "client") navigate("/portal");
    if (user && user.role === "admin") loadAll();
  }, [user, checking]); // eslint-disable-line

  const patchEnquiry = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    loadAll();
  };
  const patchProject = async (id, status) => {
    await api.patch(`/admin/projects/${id}`, { status });
    loadAll();
  };
  const patchRequest = async (id, status) => {
    await api.patch(`/admin/requests/${id}`, { status });
    loadAll();
  };

  if (checking || !overview) {
    return <div className="min-h-screen bg-[#F7F3EC] flex items-center justify-center text-[#9A9184]" data-testid="admin-loading">{t.misc.loading}</div>;
  }

  const stats = [
    { key: "new_enquiries", value: overview.new_enquiries },
    { key: "clients", value: overview.clients },
    { key: "active_projects", value: overview.active_projects },
    { key: "open_requests", value: overview.open_requests },
  ];

  return (
    <main className="min-h-screen bg-[#F7F3EC]" data-testid="admin-portal-page">
      <header className="bg-[#191713] px-6 lg:px-10 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-5">
          <span className="hidden sm:block text-xs tracking-[0.2em] uppercase text-[#B8933F]">{t.admin.title}</span>
          <button data-testid="admin-logout-button" onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 text-sm text-[#F7F3EC]/80 hover:text-[#B8933F] transition-colors">
            <LogOut size={15} /> {t.admin.logout}
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <p className="eyebrow-label">{t.admin.overview}</p>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.key} className="bg-white border border-[#191713]/10 p-6" data-testid={`admin-stat-${s.key}`}>
              <p className="font-serif text-4xl text-[#B8933F]">{s.value}</p>
              <p className="mt-2 text-xs tracking-[0.12em] uppercase text-[#9A9184]">{t.admin.stats[s.key]}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-1 border-b border-[#191713]/10">
          {["enquiries", "clients", "requests", "messages", "website", "blog"].map((k) => {
            const unreadTotal = k === "messages" ? threads.reduce((a, x) => a + x.unread, 0) : 0;
            return (
              <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                className={`px-5 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${tab === k ? "bg-[#191713] text-[#F7F3EC]" : "text-[#191713]/60 hover:text-[#191713]"}`}>
                {t.admin.tabs[k] || (k === "messages" ? "Messages" : k === "blog" ? "Blog" : "Website editor")}
                {unreadTotal > 0 && <span className="bg-[#B8933F] text-[#191713] text-[0.6rem] font-bold rounded-full w-4.5 h-4.5 px-1.5 py-0.5" data-testid="admin-unread-badge">{unreadTotal}</span>}
              </button>
            );
          })}
        </div>

        {tab === "enquiries" && (
          <div className="bg-white border border-[#191713]/10 border-t-0 overflow-x-auto" data-testid="admin-enquiries-table">
            {enquiries.length === 0 ? (
              <p className="p-8 text-sm text-[#9A9184]">{t.admin.noEnquiries}</p>
            ) : (
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="text-left text-[0.65rem] tracking-[0.15em] uppercase text-[#9A9184] border-b border-[#191713]/10">
                    <th className="px-5 py-3.5 font-semibold">{t.admin.enquiryCols.name}</th>
                    <th className="px-5 py-3.5 font-semibold">{t.admin.enquiryCols.business}</th>
                    <th className="px-5 py-3.5 font-semibold">{t.admin.enquiryCols.contact}</th>
                    <th className="px-5 py-3.5 font-semibold">{t.admin.enquiryCols.type}</th>
                    <th className="px-5 py-3.5 font-semibold">{t.admin.enquiryCols.lang}</th>
                    <th className="px-5 py-3.5 font-semibold">{t.admin.enquiryCols.received}</th>
                    <th className="px-5 py-3.5 font-semibold">{t.admin.enquiryCols.status}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#191713]/8">
                  {enquiries.map((q) => (
                    <tr key={q.id} data-testid={`admin-enquiry-row-${q.id}`}>
                      <td className="px-5 py-4 font-medium text-[#191713]">{q.name}</td>
                      <td className="px-5 py-4 text-[#191713]/70">{q.business_name || "—"}</td>
                      <td className="px-5 py-4 text-[#191713]/70">{q.email}<br /><span className="text-xs text-[#9A9184]">{q.phone}</span></td>
                      <td className="px-5 py-4 text-[#191713]/70">{q.business_type || "—"}</td>
                      <td className="px-5 py-4 text-[#191713]/70">{q.preferred_language}</td>
                      <td className="px-5 py-4 text-xs text-[#9A9184]">{new Date(q.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4">
                        <select data-testid={`admin-enquiry-status-${q.id}`} className="field-input !py-1.5 !px-2 text-xs w-32"
                          value={q.status} onChange={(e) => patchEnquiry(q.id, e.target.value)}>
                          {["new", "contacted", "closed"].map((s) => <option key={s} value={s}>{t.admin.statuses[s]}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "clients" && (
          <div className="bg-white border border-[#191713]/10 border-t-0 divide-y divide-[#191713]/8" data-testid="admin-clients-list">
            {clients.map((c) => (
              <div key={c.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`admin-client-${c.id}`}>
                <div>
                  <p className="font-medium text-[#191713]">{c.business_name || c.name}</p>
                  <p className="text-xs text-[#9A9184] mt-0.5">{c.name} · {c.email}</p>
                  {c.project && <p className="text-sm text-[#191713]/70 mt-2">{c.project.name} <span className="text-xs text-[#9A9184]">({c.project.type})</span></p>}
                </div>
                {c.project && (
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <label className="field-label" htmlFor={`stage-${c.project.id}`}>{t.admin.projectStage}</label>
                      <select id={`stage-${c.project.id}`} data-testid={`admin-project-stage-${c.project.id}`} className="field-input !py-1.5 !px-2 text-xs w-40"
                        value={c.project.status} onChange={(e) => patchProject(c.project.id, e.target.value)}>
                        {STAGES.map((s) => <option key={s} value={s}>{t.portal.stages[s]}</option>)}
                      </select>
                    </div>
                    <ProjectTools projectId={c.project.id} onDone={loadAll} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div className="bg-white border border-[#191713]/10 border-t-0 divide-y divide-[#191713]/8" data-testid="admin-requests-list">
            {requests.map((r) => (
              <div key={r.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`admin-request-${r.id}`}>
                <div>
                  <p className="text-sm font-medium text-[#191713]">
                    <span className="text-[0.6rem] tracking-[0.15em] uppercase text-[#B8933F] mr-2">{r.kind}</span>{r.subject}
                  </p>
                  <p className="text-xs text-[#9A9184] mt-1 max-w-xl">{r.message}</p>
                </div>
                <select data-testid={`admin-request-status-${r.id}`} className="field-input !py-1.5 !px-2 text-xs w-36"
                  value={r.status} onChange={(e) => patchRequest(r.id, e.target.value)}>
                  <option value="open">{t.portal.open}</option>
                  <option value="in_progress">{t.portal.inProgress}</option>
                  <option value="resolved">{t.portal.resolved}</option>
                </select>
              </div>
            ))}
          </div>
        )}
        {tab === "messages" && (
          <div className="bg-white border border-[#191713]/10 border-t-0 grid grid-cols-1 md:grid-cols-3 min-h-[420px]" data-testid="admin-messages-panel">
            <div className="border-r border-[#191713]/10 divide-y divide-[#191713]/8">
              {threads.map((th) => (
                <button key={th.client.id} data-testid={`admin-thread-${th.client.id}`} onClick={() => openThread(th.client.id)}
                  className={`w-full text-left p-4 hover:bg-[#F7F3EC]/60 transition-colors ${activeThread === th.client.id ? "bg-[#F7F3EC]" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-[#191713] truncate">{th.client.business_name || th.client.name}</p>
                    {th.unread > 0 && <span className="bg-[#B8933F] text-[#191713] text-[0.6rem] font-bold rounded-full px-1.5 py-0.5">{th.unread}</span>}
                  </div>
                  <p className="mt-1 text-xs text-[#8A8378] truncate">{th.last_message || "—"}</p>
                </button>
              ))}
              {threads.length === 0 && <p className="p-4 text-sm text-[#8A8378]">No clients yet.</p>}
            </div>
            <div className="md:col-span-2 flex flex-col">
              {activeThread ? (
                <>
                  <div className="flex-1 p-5 space-y-3 max-h-[340px] overflow-y-auto" data-testid="admin-thread-messages">
                    {threadMsgs.map((m) => (
                      <div key={m.id} className={`max-w-[80%] px-4 py-3 text-sm ${m.sender === "admin" ? "ml-auto bg-[#191713] text-[#F7F3EC]" : "bg-[#F7F3EC] text-[#191713] border border-[#191713]/10"}`}>
                        <p>{m.body}</p>
                        <p className="mt-1 text-[0.6rem] opacity-60">{new Date(m.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                    {threadMsgs.length === 0 && <p className="text-sm text-[#8A8378]">No messages in this thread yet.</p>}
                  </div>
                  <form onSubmit={sendReply} className="p-4 border-t border-[#191713]/10 flex gap-2">
                    <input data-testid="admin-reply-input" className="field-input flex-1" placeholder="Reply…"
                      value={reply} onChange={(e) => setReply(e.target.value)} />
                    <button type="submit" data-testid="admin-reply-send" className="btn-dark !py-2.5 !px-4" aria-label="Send"><Send size={15} /></button>
                  </form>
                </>
              ) : (
                <p className="m-auto text-sm text-[#8A8378]">Select a conversation.</p>
              )}
            </div>
          </div>
        )}

        {tab === "website" && <AdminCMS />}

        {tab === "blog" && <AdminBlog />}
      </div>
    </main>
  );
}

const ProjectTools = ({ projectId, onDone }) => {
  const [d, setD] = useState({ title: "", url: "" });
  const [u, setU] = useState("");
  const addDeliverable = async () => {
    if (!d.title.trim()) return;
    await api.post(`/admin/projects/${projectId}/deliverables`, d);
    setD({ title: "", url: "" });
    onDone();
  };
  const addUpdate = async () => {
    if (!u.trim()) return;
    await api.post(`/admin/projects/${projectId}/updates`, { text: u });
    setU("");
    onDone();
  };
  return (
    <div className="flex flex-wrap gap-3">
      <div className="flex gap-1 items-end">
        <input className="field-input !py-1.5 !px-2 text-xs w-36" placeholder="Deliverable title" value={d.title}
          onChange={(e) => setD({ ...d, title: e.target.value })} data-testid={`admin-deliverable-title-${projectId}`} />
        <input className="field-input !py-1.5 !px-2 text-xs w-32" placeholder="Link (optional)" value={d.url}
          onChange={(e) => setD({ ...d, url: e.target.value })} />
        <button onClick={addDeliverable} data-testid={`admin-add-deliverable-${projectId}`} className="btn-outline-dark !py-1.5 !px-2.5 text-xs"><Plus size={12} /> Deliverable</button>
      </div>
      <div className="flex gap-1 items-end">
        <input className="field-input !py-1.5 !px-2 text-xs w-44" placeholder="Important update for the client" value={u}
          onChange={(e) => setU(e.target.value)} data-testid={`admin-update-text-${projectId}`} />
        <button onClick={addUpdate} data-testid={`admin-add-update-${projectId}`} className="btn-outline-dark !py-1.5 !px-2.5 text-xs"><Plus size={12} /> Update</button>
      </div>
    </div>
  );
};
